// ============================================
// Progress Bar Component
// Shows form wizard progress
// ============================================

import React from 'react';
import { FormWizardStep } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';

interface ProgressBarProps {
  steps: FormWizardStep[];
  currentStepIndex: number;
  onStepClick?: (index: number) => void;
}

// Map wizard step IDs to i18n keys. Step ids that are PDF/form names
// (consent, care-plan, etc.) intentionally have NO entry here so they keep
// their original English title.
const STEP_TITLE_KEY: Record<string, string> = {
  'document-selection': 'step.documentSelection.title',
  'member-info': 'step.memberInfo.title',
  signature: 'step.signature.title',
};

const ProgressBar: React.FC<ProgressBarProps> = ({
  steps,
  currentStepIndex,
  onStepClick,
}) => {
  const { t } = useLanguage();
  const progress = ((currentStepIndex + 1) / steps.length) * 100;
  const percent = Math.round(progress);

  const getStepTitle = (step: FormWizardStep): string => {
    const key = STEP_TITLE_KEY[step.id];
    return key ? t(key) : step.title;
  };

  return (
    <div className="mb-8">
      {/* Progress percentage */}
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-gray-700">
          {t('progress.label')}
        </span>
        <span className="text-sm text-gray-500">
          {t('progress.complete', { percent })}
        </span>
      </div>

      {/* Progress bar */}
      <div className="w-full bg-gray-200 rounded-full h-2 mb-6">
        <div
          className="bg-blue-600 h-2 rounded-full transition-all duration-300"
          style={{ width: `${progress}%` }}
        />
      </div>

      {/* Step indicators */}
      <div className="hidden md:flex justify-between">
        {steps.map((step, index) => {
          const isCompleted = index < currentStepIndex;
          const isCurrent = index === currentStepIndex;
          const isClickable = !!onStepClick;

          return (
            <button
              key={step.id}
              type="button"
              onClick={() => isClickable && onStepClick(index)}
              disabled={!isClickable}
              className={`flex flex-col items-center group ${
                isClickable ? 'cursor-pointer' : 'cursor-default'
              }`}
            >
              {/* Step circle */}
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${
                  isCompleted
                    ? 'bg-green-500 text-white'
                    : isCurrent
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                } ${isClickable ? 'group-hover:ring-2 group-hover:ring-blue-300' : ''}`}
              >
                {index + 1}
              </div>

              {/* Step title */}
              <span
                className={`mt-2 text-xs font-medium text-center max-w-[80px] ${
                  isCurrent ? 'text-blue-600' : 'text-gray-500'
                }`}
              >
                {getStepTitle(step)}
              </span>
            </button>
          );
        })}
      </div>

      {/* Mobile: Simple step indicator */}
      <div className="flex md:hidden justify-center">
        <div className="flex items-center space-x-2">
          {steps.map((_, index) => (
            <div
              key={index}
              className={`w-2 h-2 rounded-full ${
                index < currentStepIndex
                  ? 'bg-green-500'
                  : index === currentStepIndex
                  ? 'bg-blue-600'
                  : 'bg-gray-300'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default ProgressBar;
